import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Activity, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toggleFinding, normalizeFindings, inputValue } from '@/utils/reportRules';

export const ABCDESection = ({ section, data, onChange }) => {
  const handleToggle = (optionId) => {
    const abnormalities = data?.abnormalities || [];
    const newAbnormalities = toggleFinding(normalizeFindings(abnormalities, section.id), optionId, section.id);
    
    onChange({
      ...data,
      abnormalities: newAbnormalities
    });
  };

  const handleOrientationToggle = (optionId) => {
    const orientations = data?.orientations || section.orientationOptions?.map(o => o.id) || [];
    const newOrientations = orientations.includes(optionId)
      ? orientations.filter(id => id !== optionId)
      : [...orientations, optionId];
    
    onChange({
      ...data,
      orientations: newOrientations
    });
  };

  const handleBefastToggle = (optionId) => {
    const befast = data?.befast || section.befastOptions?.map(o => o.id) || [];
    const newBefast = befast.includes(optionId)
      ? befast.filter(id => id !== optionId)
      : [...befast, optionId];
    
    onChange({
      ...data,
      befast: newBefast
    });
  };

  const handleEkgToggle = (optionId) => {
    const ekg = data?.ekg || [];
    const newEkg = toggleFinding(normalizeFindings(ekg, 'ekg'), optionId, 'ekg');
    
    onChange({
      ...data,
      ekg: newEkg
    });
  };

  const handleInputChange = (inputId, value) => {
    onChange({
      ...data,
      [inputId]: value
    });
  };

  const abnormalities = normalizeFindings(data?.abnormalities, section.id);
  const orientations = data?.orientations || section.orientationOptions?.map(o => o.id) || [];
  const befast = data?.befast || section.befastOptions?.map(o => o.id) || [];
  const ekg = normalizeFindings(data?.ekg, 'ekg');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <section.icon className={`w-6 h-6 ${section.color}`} />
        <div className="flex-1">
          <h3 className="text-xl font-bold text-foreground">{section.title}</h3>
          <p className="text-sm text-muted-foreground">{section.description}</p>
          <p className="text-xs text-muted-foreground mt-1 italic">
            Standard: {section.normalText}
          </p>
        </div>
        <Badge data-testid={`${section.id}-selection-status`} variant="outline" className="ml-auto">
          {abnormalities.length > 0 ? `${abnormalities.length} Abweichung(en)` : 'Unauffällig'}
        </Badge>
      </div>

      {/* Abnormality Options */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">Abweichungen markieren (nur wenn vorhanden)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {section.options.map((option) => (
            <div
              key={option.id}
              data-testid={`${section.id}-${option.id.replaceAll('_', '-')}-option`}
              className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-sm ${
                abnormalities.includes(option.id)
                  ? 'option-selected'
                  : 'border-border bg-card'
              }`}
              onClick={() => handleToggle(option.id)}
            >
              <Checkbox
                id={`${section.id}-${option.id}`}
                data-testid={`${section.id}-${option.id.replaceAll('_', '-')}-checkbox`}
                onClick={event => event.stopPropagation()}
                checked={abnormalities.includes(option.id)}
                onCheckedChange={() => handleToggle(option.id)}
                className="mt-0.5"
              />
              <Label
                htmlFor={`${section.id}-${option.id}`}
                onClick={event => event.stopPropagation()}
                className="text-sm font-medium cursor-pointer leading-relaxed flex-1"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Orientation Options for Disability */}
      {section.id === 'disability' && section.orientationOptions && (
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Orientierung (standardmäßig alle vorhanden)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {section.orientationOptions.map((option) => (
              <div
                key={option.id}
                data-testid={`orientation-${option.id.replaceAll('_', '-')}-option`}
                className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-sm ${
                  orientations.includes(option.id)
                    ? 'bg-[hsl(var(--success-light))] border-[hsl(var(--success))]'
                    : 'border-destructive bg-destructive/5'
                }`}
                onClick={() => handleOrientationToggle(option.id)}
              >
                <Checkbox
                  id={`${section.id}-orientation-${option.id}`}
                  data-testid={`orientation-${option.id.replaceAll('_', '-')}-checkbox`}
                  onClick={event => event.stopPropagation()}
                  checked={orientations.includes(option.id)}
                  onCheckedChange={() => handleOrientationToggle(option.id)}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`${section.id}-orientation-${option.id}`}
                  onClick={event => event.stopPropagation()}
                  className="text-sm font-medium cursor-pointer leading-relaxed flex-1"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BEFAST Test for Disability */}
      {section.id === 'disability' && section.befastOptions && (
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">BEFAST-Test (standardmäßig alle unauffällig)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {section.befastOptions.map((option) => (
              <div
                key={option.id}
                data-testid={`befast-${option.id}-option`}
                className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-sm ${
                  befast.includes(option.id)
                    ? 'bg-[hsl(var(--success-light))] border-[hsl(var(--success))]'
                    : 'border-destructive bg-destructive/5'
                }`}
                onClick={() => handleBefastToggle(option.id)}
              >
                <Checkbox
                  id={`${section.id}-befast-${option.id}`}
                  data-testid={`befast-${option.id}-checkbox`}
                  onClick={event => event.stopPropagation()}
                  checked={befast.includes(option.id)}
                  onCheckedChange={() => handleBefastToggle(option.id)}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`${section.id}-befast-${option.id}`}
                  onClick={event => event.stopPropagation()}
                  className="text-sm font-medium cursor-pointer leading-relaxed flex-1"
                >
                  {option.label} {befast.includes(option.id) ? '✓' : '✗'}
                </Label>
              </div>
            ))}
          </div>
          {section.befastInput && (
            <div className="mt-4">
              <Label htmlFor={`${section.id}-${section.befastInput.id}`} className="text-sm font-medium">
                {section.befastInput.label}
              </Label>
              <Input
                id={`${section.id}-${section.befastInput.id}`}
                data-testid="symptom-onset-input"
                type={section.befastInput.type}
                placeholder={section.befastInput.placeholder}
                value={data?.[section.befastInput.id] || ''}
                onChange={(e) => handleInputChange(section.befastInput.id, e.target.value)}
                className="h-11 mt-2"
              />
            </div>
          )}
        </div>
      )}

      {/* EKG Options for Circulation */}
      {section.id === 'circulation' && section.ekgOptions && (
        <div className="pt-4 border-t border-border">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[hsl(var(--destructive))]" />
              <h4 className="text-sm font-semibold text-foreground">EKG-Befund (optional)</h4>
            </div>
            <Link
              to="/ekg-hilfe"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ekg-section-help-link"
              aria-label="EKG-Hilfe öffnen (neuer Tab)"
              title="EKG-Hilfe in einem neuen Tab öffnen"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 min-h-11 px-3 rounded-md border border-teal-700/30 bg-primary/5 text-sm font-semibold text-teal-800 hover:bg-primary/10 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
            >
              EKG-Hilfe <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {section.ekgOptions.map((option) => (
              <div
                key={option.id}
                data-testid={`ekg-${option.id.replaceAll('_', '-')}-option`}
                className={`flex items-start space-x-3 p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-sm ${
                  ekg.includes(option.id)
                    ? 'option-selected'
                    : 'border-border bg-card'
                }`}
                onClick={() => handleEkgToggle(option.id)}
              >
                <Checkbox
                  id={`${section.id}-ekg-${option.id}`}
                  data-testid={`ekg-${option.id.replaceAll('_', '-')}-checkbox`}
                  onClick={event => event.stopPropagation()}
                  checked={ekg.includes(option.id)}
                  onCheckedChange={() => handleEkgToggle(option.id)}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`${section.id}-ekg-${option.id}`}
                  onClick={event => event.stopPropagation()}
                  className="text-sm font-medium cursor-pointer leading-relaxed flex-1"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Fields with Sliders */}
      {section.inputs && section.inputs.length > 0 && (
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3">Messwerte</h4>
          <div className="space-y-4">
            {section.inputs.map((input) => {
              const value = inputValue(data, input);
              const parsedValue = parseFloat(value);
              const numValue = Number.isFinite(parsedValue) ? parsedValue : (input.default ?? input.min ?? 0);
              
              return (
                <div key={input.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`${section.id}-${input.id}`} className="text-sm font-medium">
                      {input.label}
                    </Label>
                    <span data-testid={`${section.id}-${input.id.replaceAll('_', '-')}-value`} className="text-sm font-semibold text-primary">{value}</span>
                  </div>
                  {input.type === 'number' && input.min !== undefined && input.max !== undefined ? (
                    <>
                      <Slider
                        id={`${section.id}-${input.id}-slider`}
                        data-testid={`${section.id}-${input.id.replaceAll('_', '-')}-slider`}
                        min={input.min}
                        max={input.max}
                        step={input.step || 1}
                        value={[numValue]}
                        onValueChange={(vals) => handleInputChange(input.id, vals[0])}
                        className="w-full"
                      />
                      <Input
                        id={`${section.id}-${input.id}`}
                        data-testid={`${section.id}-${input.id.replaceAll('_', '-')}-input`}
                        type="number"
                        placeholder={value === '' ? 'Messwert eingeben' : input.placeholder}
                        step={input.step}
                        max={input.max}
                        min={input.min}
                        value={value}
                        onChange={(e) => handleInputChange(input.id, e.target.value)}
                        className="h-11"
                      />
                    </>
                  ) : (
                    <Input
                      id={`${section.id}-${input.id}`}
                      data-testid={`${section.id}-${input.id.replaceAll('_', '-')}-input`}
                      type={input.type}
                      placeholder={input.placeholder}
                      value={value}
                      onChange={(e) => handleInputChange(input.id, e.target.value)}
                      className="h-11"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ABCDESection;
